"use client";

import Link from "next/link";
import { ArrowRight, X } from "lucide-react";
import { useEffect, useState } from "react";

import { trackEvent } from "@/lib/analytics";

/**
 * Site-wide strip for the next live webinar.
 *
 * Two rules it exists to enforce, both learned from the burnout webinar, whose
 * promo kept running for two months after the event had happened:
 *
 *  1. It expires on its own. Past `endsAt` the component renders nothing, with
 *     no deploy needed, so a stale date can never sit at the top of the site.
 *  2. Dismissal persists. The previous version of this file kept it in
 *     useState, so it came back on every navigation, which is how a banner
 *     stops being an announcement and starts being furniture.
 *
 * To promote a different event, change EVENT. To run none, set it to null.
 */
const EVENT = {
  slug: "webinar-limite",
  href: "/webinar-limite",
  /** Full ISO with the Romanian offset. */
  endsAt: "2026-09-30T20:30:00+03:00",
  eyebrow: "Webinar gratuit",
  title: "De ce ne e atât de greu să punem limite?",
  when: "Miercuri, 30 septembrie · 19:00",
  whenShort: "30 sept, 19:00",
} as const;

const dismissKey = `mindtale_banner_dismissed_${EVENT.slug}`;

export default function AnnouncementBanner() {
  // Rendered only after mount: the decision depends on the current time and on
  // localStorage, neither of which the server can know, and a mismatch here
  // would hydrate the whole page wrong.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (Date.now() > new Date(EVENT.endsAt).getTime()) return;
    try {
      if (window.localStorage.getItem(dismissKey) === "1") return;
    } catch {
      // Private mode or blocked storage: show it rather than hide it.
    }
    setVisible(true);
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    try {
      window.localStorage.setItem(dismissKey, "1");
    } catch {
      // Not persisting a dismissal is survivable; crashing the page is not.
    }
    trackEvent("banner_dismiss", { banner: EVENT.slug });
  };

  return (
    <div className="relative bg-[#264653] text-white">
      {/* One line at every width. Below sm the long title is dropped rather
          than wrapped: a top bar that runs to two lines stops reading as a
          strip and starts eating the page. The accessible name carries the
          full sentence either way. */}
      <Link
        href={EVENT.href}
        onClick={() => trackEvent("banner_click", { banner: EVENT.slug })}
        aria-label={`${EVENT.eyebrow}: ${EVENT.title} ${EVENT.when}. Rezervă locul.`}
        className="group mx-auto flex max-w-[1240px] items-center justify-center gap-x-2.5 py-2.5 pl-4 pr-12 sm:gap-x-3 sm:px-16"
      >
        <span aria-hidden className="contents">
          {/* Phones */}
          <span className="truncate text-[13px] font-medium sm:hidden">
            <span className="font-semibold text-[#6FBFC3]">Webinar gratuit</span>
            {" · "}
            {EVENT.whenShort}
          </span>

          {/* sm and up */}
          <span className="hidden shrink-0 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#6FBFC3] sm:inline">
            {EVENT.eyebrow}
          </span>
          <span className="hidden text-[14px] font-medium sm:inline">
            {EVENT.title}
          </span>
          <span className="hidden shrink-0 text-[13px] text-white/60 md:inline">
            {EVENT.when}
          </span>

          <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-[13px] font-semibold text-[#6FBFC3]">
            <span className="hidden sm:inline">Rezervă locul</span>
            <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
          </span>
        </span>
      </Link>

      <button
        type="button"
        onClick={dismiss}
        aria-label="Închide anunțul"
        className="absolute right-1 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-full text-white/50 transition hover:bg-white/10 hover:text-white sm:right-2"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}
